// src/pages/About.tsx
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import LibraryMusicRounded from '@mui/icons-material/LibraryMusicRounded';
import OndemandVideoRounded from '@mui/icons-material/OndemandVideoRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import WorkRounded from '@mui/icons-material/WorkRounded';
import PasswordRounded from '@mui/icons-material/PasswordRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import FlashOnRounded from '@mui/icons-material/FlashOnRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import AlarmRounded from '@mui/icons-material/AlarmRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import DevicesRounded from '@mui/icons-material/DevicesRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { useAuthShell } from '../components/layout/useAuthShell';

const float1 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-30px,24px) scale(1.12)}`;
const float2 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(36px,-20px) scale(.92)}`;

type Grad = [string, string];

const STATS: { value: string; label: string; icon: React.ComponentType<any>; c: Grad }[] = [
  { value: '12+', label: 'Tools in one app', icon: FlashOnRounded, c: ['#6226A7', '#B06AB3'] },
  { value: 'Web + Mobile', label: 'Same account everywhere', icon: DevicesRounded, c: ['#2563EB', '#60A5FA'] },
  { value: 'Real-time', label: 'Chat with friends', icon: ChatRounded, c: ['#00B4DB', '#0083B0'] },
  { value: 'Secure', label: 'Protected sign-in', icon: LockRounded, c: ['#11998E', '#38EF7D'] },
];

const TOOLS: { title: string; text: string; icon: React.ComponentType<any>; c: Grad }[] = [
  { title: 'Notes', text: 'Rich text, categories, tags, reminders and attachments.', icon: StickyNote2Rounded, c: ['#6226A7', '#B06AB3'] },
  { title: 'Payments', text: 'Income, expenses and transfers with receipts and monthly totals.', icon: PaymentsRounded, c: ['#11998E', '#38EF7D'] },
  { title: 'Grocery', text: 'A shopping list with categories, quantities and a progress bar.', icon: ShoppingCartRounded, c: ['#FF9800', '#E65100'] },
  { title: 'Chat', text: 'Message friends and share images, videos and files.', icon: ChatRounded, c: ['#00B4DB', '#0083B0'] },
  { title: 'Tunes', text: 'Upload music, build playlists and play anywhere.', icon: LibraryMusicRounded, c: ['#4776E6', '#8E54E9'] },
  { title: 'Videos', text: 'Password-protected videos and YouTube / Vimeo links.', icon: OndemandVideoRounded, c: ['#EB3349', '#F45C43'] },
  { title: 'Documents', text: 'Folders and files kept in one tidy place.', icon: FolderRounded, c: ['#CF1E52', '#FF6B8B'] },
  { title: 'Calendar', text: 'Every reminder and due payment in one monthly view.', icon: CalendarMonthRounded, c: ['#2563EB', '#60A5FA'] },
  { title: 'Projects', text: 'Tasks, timelines and time tracking for teams.', icon: WorkRounded, c: ['#373B44', '#4286F4'] },
  { title: 'Reports', text: 'Charts and trends across your notes and money.', icon: BarChartRounded, c: ['#0F766E', '#2DD4BF'] },
  { title: 'Password generator', text: 'Strong, random passwords in one click.', icon: PasswordRounded, c: ['#6226A7', '#B06AB3'] },
  { title: 'Speed test', text: 'Download, upload and latency on the Cloudflare edge.', icon: SpeedRounded, c: ['#1E3A8A', '#2563EB'] },
];

const STEPS = [
  { n: '1', title: 'Sign in once', text: 'One account unlocks every tool, on the web and on your phone.' },
  { n: '2', title: 'Capture everything', text: 'Write a note, log a payment, add groceries. It takes seconds.' },
  { n: '3', title: 'Stay on top of it', text: 'Reminders, search and reports keep you ahead of your day.' },
];

const VALUES: { title: string; text: string; icon: React.ComponentType<any>; c: Grad }[] = [
  { title: 'Fast', text: 'A quick editor, instant search and keyboard shortcuts.', icon: FlashOnRounded, c: ['#F59E0B', '#FBBF24'] },
  { title: 'Findable', text: 'Search across titles and content, with date and category filters.', icon: SearchRounded, c: ['#2563EB', '#60A5FA'] },
  { title: 'Reliable', text: 'Reminders and due dates so nothing slips past you.', icon: AlarmRounded, c: ['#FF9800', '#E65100'] },
  { title: 'In sync', text: 'The same data on every device you sign in from.', icon: SyncRounded, c: ['#11998E', '#38EF7D'] },
  { title: 'Private', text: 'Your data is behind a protected, token-based sign-in.', icon: LockRounded, c: ['#6226A7', '#B06AB3'] },
  { title: 'Delightful', text: 'A colourful, glossy design that is a pleasure to use.', icon: PaletteRounded, c: ['#EC008C', '#FC6767'] },
];

const Tile = ({ c, children, size = 52 }: { c: Grad; children: React.ReactNode; size?: number }) => (
  <Box
    sx={{
      width: size, height: size, borderRadius: '18px', display: 'grid', placeItems: 'center', color: '#fff', flexShrink: 0,
      background: `linear-gradient(135deg, ${c[0]}, ${c[1]})`,
      boxShadow: `0 10px 20px ${c[1]}55, inset 0 1px 0 rgba(255,255,255,.45)`,
    }}
  >
    {children}
  </Box>
);

const SectionTitle = ({ kicker, title, sub }: { kicker: string; title: string; sub?: string }) => (
  <Box sx={{ textAlign: 'center', mb: 3 }}>
    <Typography sx={{ fontWeight: 900, fontSize: 13, letterSpacing: 1.4, textTransform: 'uppercase', color: '#6226A7' }}>
      {kicker}
    </Typography>
    <Typography sx={{ fontWeight: 900, fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.02em', lineHeight: 1.15, mt: 0.5 }}>
      {title}
    </Typography>
    {sub && <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 620, mx: 'auto' }}>{sub}</Typography>}
  </Box>
);

export default function About() {
  const navigate = useNavigate();
  const { isAuthed } = useAuthShell();
  const ctaLabel = isAuthed ? 'Go to dashboard' : 'Get started';
  const ctaPath = isAuthed ? '/dashboard' : '/login';

  return (
    <Box sx={{ width: '100%', maxWidth: 1536, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, md: 4 } }}>
      {/* ===== Hero ===== */}
      <Box
        className="qs-animate-in"
        sx={{
          position: 'relative', overflow: 'hidden', color: '#fff', borderRadius: '36px',
          p: { xs: 3.5, md: 7 }, textAlign: 'center',
          background: 'linear-gradient(120deg, #2E0F5E 0%, #6226A7 32%, #B13FD6 62%, #F6A938 100%)',
          backgroundSize: '220% 220%',
          boxShadow: '0 30px 60px rgba(98,38,167,.38), inset 0 0 0 1px rgba(255,255,255,.25)',
          '@media (prefers-reduced-motion: no-preference)': { animation: 'qsGradientShift 14s ease infinite' },
        }}
      >
        <Box sx={{ position: 'absolute', width: 420, height: 420, right: -100, top: -160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,214,122,.6), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${float1} 12s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', width: 460, height: 460, left: -140, bottom: -240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,111,216,.5), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${float2} 14s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.22) 0%, rgba(255,255,255,0) 40%)' }} />

        <Box sx={{ position: 'relative', maxWidth: 820, mx: 'auto' }}>
          <Box
            sx={{
              display: 'inline-block', px: 1.75, py: 0.6, borderRadius: '999px', mb: 2, fontWeight: 800, fontSize: 13, letterSpacing: 0.4,
              background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.35)', backdropFilter: 'blur(8px)',
            }}
          >
            ✨ About QuillStack
          </Box>
          <Typography
            component="h1"
            sx={{ fontWeight: 900, fontSize: { xs: 34, md: 58 }, lineHeight: 1.06, letterSpacing: '-0.03em', textShadow: '0 6px 24px rgba(0,0,0,.25)' }}
          >
            One calm place for{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg,#FFE29A,#FFFFFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              everything you keep track of.
            </Box>
          </Typography>
          <Typography sx={{ mt: 2.5, fontSize: { xs: 16, md: 20 }, opacity: 0.95, lineHeight: 1.6 }}>
            QuillStack helps you capture ideas fast, find them instantly, and stay on top of your day — notes, money,
            shopping, chats, music and videos, together in one fast and colourful workspace.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
            <Button
              onClick={() => navigate(ctaPath)}
              endIcon={<ArrowForwardRounded />}
              sx={{
                px: 3.5, py: 1.5, borderRadius: '999px', fontWeight: 900, fontSize: 16, color: '#4a1d86',
                background: 'linear-gradient(180deg,#fff,#EFE3FF)', boxShadow: '0 14px 28px rgba(0,0,0,.3), inset 0 1px 0 #fff',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              {ctaLabel}
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              sx={{
                px: 3.5, py: 1.5, borderRadius: '999px', fontWeight: 800, fontSize: 16, color: '#fff',
                border: '2px solid rgba(255,255,255,.75)',
                '&:hover': { bgcolor: 'rgba(255,255,255,.16)', transform: 'translateY(-2px)' },
              }}
            >
              Contact us
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ===== Stats ===== */}
      <Box sx={{ display: 'grid', gap: 2, mt: 3, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
        {STATS.map((s, i) => {
          const Icon = s.icon;
          return (
            <Box
              key={s.label}
              className="qs-animate-in"
              sx={{
                position: 'relative', overflow: 'hidden', color: '#fff', borderRadius: '24px', p: 2.25, minHeight: 132,
                background: `linear-gradient(135deg, ${s.c[0]}, ${s.c[1]})`,
                boxShadow: `0 14px 28px ${s.c[1]}55, inset 0 0 0 1px rgba(255,255,255,.35)`,
                animationDelay: `${i * 0.06}s`,
                '&::before': {
                  content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,.38), rgba(255,255,255,.06) 42%, rgba(255,255,255,0) 43%, rgba(0,0,0,.12))',
                },
              }}
            >
              <Icon sx={{ position: 'absolute', right: -10, bottom: -16, fontSize: 100, color: 'rgba(255,255,255,.18)', transform: 'rotate(-12deg)' }} />
              <Stack sx={{ position: 'relative', height: '100%' }} justifyContent="flex-end">
                <Typography sx={{ fontWeight: 900, fontSize: { xs: 24, md: 30 }, lineHeight: 1.1, textShadow: '0 2px 8px rgba(0,0,0,.25)' }}>{s.value}</Typography>
                <Typography sx={{ fontWeight: 700, opacity: 0.95 }}>{s.label}</Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* ===== Our story ===== */}
      <Box sx={{ mt: { xs: 5, md: 8 }, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'center' }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 13, letterSpacing: 1.4, textTransform: 'uppercase', color: '#6226A7' }}>Our story</Typography>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.02em', lineHeight: 1.15, mt: 0.5, mb: 2 }}>
            Built to replace a dozen scattered apps.
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 17, lineHeight: 1.7, mb: 1.5 }}>
            Ideas live in one app, bills in another, shopping lists in a third. QuillStack started as a simple note
            keeper and grew into a single workspace for the things you actually keep track of every day.
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 17, lineHeight: 1.7 }}>
            The goal stays the same: make capturing quick, finding instant, and the whole thing a pleasure to look at.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            position: 'relative', overflow: 'hidden', borderRadius: '30px', p: { xs: 2.5, md: 3.5 },
            border: '1px solid rgba(98,38,167,.14)', boxShadow: '0 20px 44px rgba(98,38,167,.14)',
            background: 'linear-gradient(180deg,#fff,#FBF8FF)',
            '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 6, background: 'linear-gradient(90deg,#6226A7,#EC4899,#F6A938)' },
          }}
        >
          <Stack spacing={2}>
            {[
              { icon: StickyNote2Rounded, c: ['#6226A7', '#B06AB3'] as Grad, t: 'Capture', d: 'Notes, payments and lists in seconds.' },
              { icon: SearchRounded, c: ['#2563EB', '#60A5FA'] as Grad, t: 'Find', d: 'Search everything from one place.' },
              { icon: AlarmRounded, c: ['#FF9800', '#E65100'] as Grad, t: 'Remember', d: 'Reminders that keep you on track.' },
            ].map((r) => {
              const Icon = r.icon;
              return (
                <Stack key={r.t} direction="row" spacing={2} alignItems="center">
                  <Tile c={r.c}><Icon /></Tile>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{r.t}</Typography>
                    <Typography color="text.secondary">{r.d}</Typography>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      </Box>

      {/* ===== What's inside ===== */}
      <Box sx={{ mt: { xs: 5, md: 8 } }}>
        <SectionTitle kicker="What's inside" title="Every tool you need, ready to use" sub="Each section has its own colour so you always know where you are." />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' } }}>
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Paper
                key={t.title}
                elevation={0}
                className="qs-animate-in"
                sx={{
                  p: 2.25, borderRadius: '24px', animationDelay: `${(i % 8) * 0.04}s`,
                  border: '1px solid', borderColor: `${t.c[0]}33`, boxShadow: `0 10px 24px ${t.c[1]}22`,
                  transition: 'transform .25s ease, box-shadow .25s ease',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 20px 36px ${t.c[1]}44` },
                }}
              >
                <Tile c={t.c}><Icon /></Tile>
                <Typography sx={{ fontWeight: 900, fontSize: 18, mt: 1.5 }}>{t.title}</Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, lineHeight: 1.55 }}>{t.text}</Typography>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* ===== How it works ===== */}
      <Box sx={{ mt: { xs: 5, md: 8 } }}>
        <SectionTitle kicker="How it works" title="Up and running in three steps" />
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {STEPS.map((st) => (
            <Paper
              key={st.n}
              elevation={0}
              sx={{ p: 3, borderRadius: '28px', textAlign: 'center', border: '1px solid rgba(98,38,167,.12)', boxShadow: '0 14px 32px rgba(98,38,167,.1)' }}
            >
              <Box
                sx={{
                  width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center',
                  fontWeight: 900, fontSize: 28, color: '#fff',
                  background: 'linear-gradient(135deg,#6226A7,#B06AB3)',
                  boxShadow: '0 12px 24px rgba(98,38,167,.4), inset 0 1px 0 rgba(255,255,255,.45)',
                }}
              >
                {st.n}
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: 20 }}>{st.title}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>{st.text}</Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* ===== Values ===== */}
      <Box sx={{ mt: { xs: 5, md: 8 } }}>
        <SectionTitle kicker="Why QuillStack" title="Small details that make a difference" />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <Paper
                key={v.title}
                elevation={0}
                sx={{
                  p: 2.5, borderRadius: '24px', display: 'flex', gap: 2, alignItems: 'flex-start',
                  transition: 'transform .25s ease, box-shadow .25s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 20px 36px ${v.c[1]}44` },
                }}
              >
                <Tile c={v.c}><Icon /></Tile>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{v.title}</Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.55 }}>{v.text}</Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* ===== Call to action ===== */}
      <Box
        sx={{
          mt: { xs: 5, md: 8 }, position: 'relative', overflow: 'hidden', borderRadius: '32px', p: { xs: 3.5, md: 6 }, textAlign: 'center',
          color: '#fff',
          background: 'linear-gradient(120deg, #2E0F5E 0%, #6226A7 40%, #B13FD6 75%, #F6A938 130%)',
          boxShadow: '0 26px 54px rgba(98,38,167,.35), inset 0 0 0 1px rgba(255,255,255,.25)',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.24), rgba(255,255,255,0) 45%)' }} />
        <Typography sx={{ position: 'relative', fontWeight: 900, fontSize: { xs: 26, md: 38 }, letterSpacing: '-0.02em' }}>
          Ready to try QuillStack?
        </Typography>
        <Typography sx={{ position: 'relative', mt: 1, mb: 3, opacity: 0.95, fontSize: 18 }}>
          Sign in and bring your whole day into one place.
        </Typography>
        <Button
          onClick={() => navigate(ctaPath)}
          endIcon={<ArrowForwardRounded />}
          sx={{
            position: 'relative', px: 4, py: 1.5, borderRadius: '999px', fontWeight: 900, fontSize: 16, color: '#4a1d86',
            background: 'linear-gradient(180deg,#fff,#EFE3FF)', boxShadow: '0 14px 28px rgba(0,0,0,.3), inset 0 1px 0 #fff',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          {ctaLabel}
        </Button>
      </Box>
    </Box>
  );
}
